import { Injectable } from '@angular/core';
import {
  getAccount,
  getPublicClient,
  getWalletClient,
  readContract,
  simulateContract,
} from '@wagmi/core';
import { getAddress, isAddress } from 'viem';

import { environment } from 'src/environments/environment';
import { onChainBidNoun } from '@/utils/native-market-bid-labels';
import {
  isPunksmarketRouterAddress,
  PUNKSMARKET_V1_ROUTER_ADDRESS,
} from '@/constants/punksmarket-v1';
import { Web3Service } from './web3.service';

import CryptoPunksMarketABI from '../../abi/CryptoPunksMarket.json';
import PunksMarketABI from '../../abi/PunksMarket.json';
import { PunksmarketIndexerCriteria } from '@/models/unified-market-bid';

export interface PunksFilterTuple {
  requiredTraitMask: bigint;
  forbiddenTraitMask: bigint;
  anyOfTraitMask: bigint;
  requiredColorMask: bigint;
  forbiddenColorMask: bigint;
  anyOfColorMask: bigint;
  minPixelCount: number;
  maxPixelCount: number;
  minColorCount: number;
  maxColorCount: number;
}

export function emptyPunksFilter(): PunksFilterTuple {
  return {
    requiredTraitMask: 0n,
    forbiddenTraitMask: 0n,
    anyOfTraitMask: 0n,
    requiredColorMask: 0n,
    forbiddenColorMask: 0n,
    anyOfColorMask: 0n,
    minPixelCount: 0,
    maxPixelCount: 0,
    minColorCount: 0,
    maxColorCount: 0,
  };
}

export function criteriaFromIndexer(
  c?: PunksmarketIndexerCriteria,
): PunksFilterTuple {
  const parseMask = (v?: string) => {
    if (!v || v === '0') {
      return 0n;
    }
    return BigInt(v);
  };
  return {
    requiredTraitMask: parseMask(c?.requiredTraitMask),
    forbiddenTraitMask: parseMask(c?.forbiddenTraitMask),
    anyOfTraitMask: parseMask(c?.anyOfTraitMask),
    requiredColorMask: parseMask(c?.requiredColorMask),
    forbiddenColorMask: parseMask(c?.forbiddenColorMask),
    anyOfColorMask: parseMask(c?.anyOfColorMask),
    minPixelCount: c?.minPixelCount ?? 0,
    maxPixelCount: c?.maxPixelCount ?? 0,
    minColorCount: c?.minColorCount ?? 0,
    maxColorCount: c?.maxColorCount ?? 0,
  };
}

export interface DirectedV1Listing {
  isForSale: boolean;
  punkIndex: bigint;
  seller: string;
  minValue: bigint;
  onlySellTo: string;
}

export class PunksmarketListingPriceMismatchError extends Error {
  constructor(
    public readonly expectedWei: bigint,
    public readonly actualWei: bigint,
  ) {
    super(
      `Listing price changed: expected ${expectedWei} wei, on-chain ${actualWei} wei`,
    );
    this.name = 'PunksmarketListingPriceMismatchError';
  }
}

export class PunksmarketListingNotValidError extends Error {
  constructor(message = 'This punk is not listed for sale on PunksMarket') {
    super(message);
    this.name = 'PunksmarketListingNotValidError';
  }
}

@Injectable({
  providedIn: 'root',
})
export class PunksmarketV1Service {
  private readonly nativeV1Address =
    (environment.v1PunksMarketAddress ??
      '0x6ba6f2207e343923ba692e5cae646fb0f566db8d') as `0x${string}`;

  private readonly routerAddress = (environment.punksmarketV1RouterAddress ??
    PUNKSMARKET_V1_ROUTER_ADDRESS) as `0x${string}`;

  constructor(private readonly web3Svc: Web3Service) {}

  isEnabled(): boolean {
    return (
      environment.collection === 'cryptopunks-v1' &&
      environment.featureFlags?.PUNKSMARKET_NATIVE_TRADING !== false
    );
  }

  isBidSupportEnabled(): boolean {
    return (
      environment.collection === 'cryptopunks-v1' &&
      environment.featureFlags?.PUNKSMARKET_BID_SUPPORT !== false
    );
  }

  isBidNativeEnabled(): boolean {
    return (
      this.isBidSupportEnabled() &&
      environment.featureFlags?.PUNKSMARKET_BID_NATIVE !== false
    );
  }

  /**
   * Reads the live directed listing on native Larva V1 (June 2017) and validates
   * it targets the PunksMarket router.
   */
  async getDirectedListing(punkId: string): Promise<DirectedV1Listing | null> {
    const publicClient = getPublicClient(this.web3Svc.config);
    if (!publicClient) {
      throw new Error('No public client');
    }

    const index = BigInt(punkId);
    const offer = (await readContract(this.web3Svc.config, {
      address: this.nativeV1Address,
      abi: CryptoPunksMarketABI,
      functionName: 'punksOfferedForSale',
      args: [index],
    })) as readonly [boolean, bigint, string, bigint, string];

    const [isForSale, punkIndex, seller, minValue, onlySellTo] = offer;
    if (!isForSale || !isPunksmarketRouterAddress(onlySellTo)) {
      return null;
    }

    const owner = (await readContract(this.web3Svc.config, {
      address: this.nativeV1Address,
      abi: CryptoPunksMarketABI,
      functionName: 'punkIndexToAddress',
      args: [index],
    })) as string;

    if (getAddress(seller) !== getAddress(owner)) {
      return null;
    }

    return {
      isForSale,
      punkIndex,
      seller: getAddress(seller),
      minValue,
      onlySellTo: getAddress(onlySellTo),
    };
  }

  async listUnwrappedPunk(
    punkId: string,
    priceEth: number,
  ): Promise<string | undefined> {
    if (!priceEth || priceEth <= 0) {
      throw new Error('Listing price must be greater than zero');
    }

    const weiValue = this.web3Svc.ethToWei(priceEth);
    return this.writeNativeV1('offerPunkForSaleToAddress', [
      BigInt(punkId),
      weiValue,
      this.routerAddress,
    ]);
  }

  async delistUnwrappedPunk(punkId: string): Promise<string | undefined> {
    return this.writeNativeV1('punkNoLongerForSale', [BigInt(punkId)]);
  }

  /**
   * Buys a directed listing via the PunksMarket router. Re-reads on-chain price
   * before sending the transaction.
   */
  async buyListedPunk(
    punkId: string,
    expectedListingWei: bigint | string,
    recipient?: string,
  ): Promise<string | undefined> {
    const listing = await this.getDirectedListing(punkId);
    if (!listing) {
      throw new PunksmarketListingNotValidError();
    }

    const expectedWei = BigInt(expectedListingWei);
    if (listing.minValue !== expectedWei) {
      throw new PunksmarketListingPriceMismatchError(
        expectedWei,
        listing.minValue,
      );
    }

    const account = getAccount(this.web3Svc.config);
    const to = recipient ?? account.address;
    if (!to || !isAddress(to)) {
      throw new Error('Wallet not connected');
    }

    const network = await this.web3Svc.checkNetwork();
    const walletClient = await getWalletClient(this.web3Svc.config, {
      chainId: network.chain?.id,
    });
    if (!walletClient) {
      throw new Error('No wallet client');
    }

    const punkIndex = Number(punkId);
    if (punkIndex < 0 || punkIndex >= 10_000) {
      throw new Error('Invalid punk id');
    }

    const { request } = await simulateContract(this.web3Svc.config, {
      address: this.routerAddress,
      abi: PunksMarketABI,
      functionName: 'buyPunk',
      args: [punkIndex, expectedWei, getAddress(to)],
      value: expectedWei,
      account: account.address,
    });

    return walletClient.writeContract(request);
  }

  async placeBid(params: {
    bidWei: bigint;
    settlementWei?: bigint;
    criteria?: PunksFilterTuple;
    includeIds?: number[];
    excludeIds?: number[];
  }): Promise<string | undefined> {
    if (!this.isBidNativeEnabled()) {
      throw new Error(`${onChainBidNoun} placement is disabled`);
    }
    const settlementWei = params.settlementWei ?? 0n;
    const value = params.bidWei + settlementWei;
    if (params.bidWei <= 0n) {
      throw new Error('Bid amount must be greater than zero');
    }
    return this.writeRouter('placeBid', [
      params.bidWei,
      settlementWei,
      params.criteria ?? emptyPunksFilter(),
      (params.includeIds ?? []).map((id) => id),
      (params.excludeIds ?? []).map((id) => id),
    ], value);
  }

  async cancelBid(bidId: string | bigint): Promise<string | undefined> {
    if (!this.isBidNativeEnabled()) {
      throw new Error(`${onChainBidNoun} cancellation is disabled`);
    }
    return this.writeRouter('cancelBid', [BigInt(bidId)]);
  }

  async adjustBidPrice(
    bidId: string | bigint,
    weiToAdjust: bigint,
    increase: boolean,
  ): Promise<string | undefined> {
    if (!this.isBidNativeEnabled()) {
      throw new Error(`${onChainBidNoun} adjustment is disabled`);
    }
    const value = increase ? weiToAdjust : 0n;
    return this.writeRouter(
      'adjustBidPrice',
      [BigInt(bidId), weiToAdjust, increase],
      value,
    );
  }

  /**
   * Seller (or settler) accepts a PunksMarket bid against a directed listing.
   */
  async acceptBid(
    bidId: string | bigint,
    punkId: string,
    expectedListingWei?: bigint,
  ): Promise<string | undefined> {
    if (!this.isBidNativeEnabled()) {
      throw new Error(`Accept ${onChainBidNoun} is disabled`);
    }
    const listing = await this.getDirectedListing(punkId);
    if (!listing) {
      throw new PunksmarketListingNotValidError(
        `List this punk for sale to punksmarket.eth before accepting an ${onChainBidNoun}`,
      );
    }
    const listingWei = expectedListingWei ?? listing.minValue;
    if (listing.minValue !== listingWei) {
      throw new PunksmarketListingPriceMismatchError(
        listingWei,
        listing.minValue,
      );
    }
    const punkIndex = Number(punkId);
    return this.writeRouter('acceptBid', [
      BigInt(bidId),
      punkIndex,
      listingWei,
    ]);
  }

  async withdrawCredits(): Promise<string | undefined> {
    if (!this.isBidNativeEnabled()) {
      throw new Error('PunksMarket withdraw is disabled');
    }
    return this.writeRouter('withdraw', []);
  }

  async getPendingWithdrawal(account: string): Promise<bigint> {
    const publicClient = getPublicClient(this.web3Svc.config);
    if (!publicClient) {
      return 0n;
    }
    const bal = (await readContract(this.web3Svc.config, {
      address: this.routerAddress,
      abi: PunksMarketABI,
      functionName: 'balances',
      args: [getAddress(account)],
    })) as bigint;
    return bal;
  }

  private async writeRouter(
    functionName: string,
    args: unknown[],
    value = 0n,
  ): Promise<string | undefined> {
    const account = getAccount(this.web3Svc.config);
    if (!account.address) {
      throw new Error('Wallet not connected');
    }
    const network = await this.web3Svc.checkNetwork();
    const walletClient = await getWalletClient(this.web3Svc.config, {
      chainId: network.chain?.id,
    });
    if (!walletClient) {
      throw new Error('No wallet client');
    }

    const { request } = await simulateContract(this.web3Svc.config, {
      address: this.routerAddress,
      abi: PunksMarketABI,
      functionName,
      args,
      value,
      account: account.address,
    });

    return walletClient.writeContract(request);
  }

  private async writeNativeV1(
    functionName: string,
    args: unknown[],
  ): Promise<string | undefined> {
    const network = await this.web3Svc.checkNetwork();
    const walletClient = await getWalletClient(this.web3Svc.config, {
      chainId: network.chain?.id,
    });
    if (!walletClient) {
      throw new Error('No wallet client');
    }

    const tx = {
      address: this.nativeV1Address,
      abi: CryptoPunksMarketABI,
      functionName,
      args,
    } as const;

    return walletClient.writeContract(tx);
  }
}
